import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import emailjs from '@emailjs/browser';
import { FiPhone, FiMail, FiMapPin, FiClock, FiSend, FiCheck, FiAlertCircle } from 'react-icons/fi';
import colors from '../styles/colors';
import usePageMeta from '../hooks/usePageMeta';

const PageWrapper = styled.div`
  padding-top: 80px;
`;

const HeroSection = styled.section`
  padding: 80px 20px;
  background: linear-gradient(135deg, ${colors.background.darker} 0%, ${colors.background.medium} 100%);
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 700px;
  margin: 0 auto;
`;

const Label = styled.div`
  font-family: 'Oswald', sans-serif;
  font-size: 0.9rem;
  color: ${colors.accent.main};
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 15px;
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 6vw, 4rem);
  color: ${colors.text.heading};
  margin-bottom: 20px;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: ${colors.text.light};
  line-height: 1.7;
`;

const Section = styled.section`
  padding: 100px 20px;
  background: ${colors.background.medium};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 60px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 50px;
  }
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
`;

const InfoCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 25px;
  background: ${colors.metallic.darkSteel};
  border: 1px solid ${colors.borderLight};
  border-radius: 12px;
  transition: all 0.3s ease;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 4px 15px rgba(0, 0, 0, 0.3);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(180deg, rgba(255,255,255,0.03) 0%, transparent 50%);
    pointer-events: none;
    border-radius: 12px;
  }

  &:hover {
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.12), 0 15px 40px rgba(0, 0, 0, 0.4);
    transform: translateY(-3px);
    border-color: ${colors.primary.dark};
  }
`;

const IconBox = styled.div`
  width: 50px;
  height: 50px;
  background: ${colors.metallic.steel};
  border: 1px solid ${colors.borderLight};
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.15), 0 2px 8px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;

  svg {
    font-size: 1.3rem;
    color: ${colors.primary.light};
  }

  ${InfoCard}:hover & {
    background: ${colors.accent.gradient};
    border-color: ${colors.accent.dark};
    box-shadow: 0 0 20px rgba(0, 102, 177, 0.3);

    svg {
      color: ${colors.text.white};
    }
  }
`;

const InfoContent = styled.div``;

const InfoLabel = styled.div`
  font-size: 0.8rem;
  color: ${colors.text.muted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 6px;
`;

const InfoValue = styled.div`
  font-size: 1.1rem;
  color: ${colors.text.heading};
  font-weight: 600;

  a {
    color: ${colors.text.heading};
    transition: color 0.3s ease;

    &:hover {
      color: ${colors.accent.light};
    }
  }
`;

const Form = styled.form`
  background: ${colors.metallic.darkSteel};
  padding: 45px;
  border-radius: 12px;
  border: 1px solid ${colors.borderLight};
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05), 0 10px 40px rgba(0, 0, 0, 0.4);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(180deg, rgba(255,255,255,0.02) 0%, transparent 30%);
    pointer-events: none;
    border-radius: 12px;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Input = styled.input`
  width: 100%;
  padding: 16px 20px;
  background: ${colors.background.card};
  border: 1px solid ${colors.borderLight};
  border-radius: 8px;
  font-size: 1rem;
  color: ${colors.text.heading};
  transition: all 0.3s ease;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.2);

  &::placeholder {
    color: ${colors.text.muted};
  }

  &:focus {
    outline: none;
    border-color: ${colors.primary.light};
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.2), 0 0 0 3px rgba(156, 163, 175, 0.2);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 16px 20px;
  background: ${colors.background.card};
  border: 1px solid ${colors.borderLight};
  border-radius: 8px;
  font-size: 1rem;
  color: ${colors.text.heading};
  resize: vertical;
  min-height: 140px;
  font-family: inherit;
  transition: all 0.3s ease;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.2);

  &::placeholder {
    color: ${colors.text.muted};
  }

  &:focus {
    outline: none;
    border-color: ${colors.primary.light};
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.2), 0 0 0 3px rgba(156, 163, 175, 0.2);
  }
`;

const SubmitBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 18px;
  background: ${colors.accent.gradient};
  color: ${colors.text.white};
  font-family: 'Oswald', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-radius: 8px;
  border: 1px solid ${colors.accent.light};
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.2), 0 4px 20px rgba(0, 102, 177, 0.4);
  position: relative;
  z-index: 1;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-3px);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.3), 0 8px 30px rgba(0, 102, 177, 0.5);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const StatusMsg = styled.div`
  text-align: center;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: ${({ $success }) => $success ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)'};
  color: ${({ $success }) => $success ? '#22c55e' : '#ef4444'};
  border: 1px solid ${({ $success }) => $success ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'};
`;

const MapSection = styled.section`
  padding: 0;
  height: 400px;
  background: ${colors.background.card};

  iframe {
    width: 100%;
    height: 100%;
    border: none;
    filter: grayscale(50%);
  }
`;

export default function ContactPage() {
  usePageMeta(
    'Contact Us | (802) 458-7848 | The Honest Mechanic, Charlotte, VT',
    'Contact The Honest Mechanic in Charlotte, VT. Call (802) 458-7848 or email thehonestmechanicvt@gmail.com. Located at 5692 Ethan Allen Hwy. Mon-Fri 9am-5pm.'
  );

  const formRef = useRef();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSending(true);
    setStatus(null);

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (!serviceId || !templateId || !publicKey) {
      setStatus('error');
      setSending(false);
      return;
    }

    emailjs.sendForm(serviceId, templateId, formRef.current, publicKey)
      .then(() => {
        setStatus('success');
        setForm({ name: '', email: '', phone: '', message: '' });
      })
      .catch(() => {
        setStatus('error');
      })
      .finally(() => {
        setSending(false);
      });
  };

  return (
    <PageWrapper>
      <HeroSection>
        <HeroContent>
          <Label>Get In Touch</Label>
          <Title>Contact Us</Title>
          <Subtitle>
            Ready to schedule service? Have questions? Give us a call or drop us a message.
            We're here to help.
          </Subtitle>
        </HeroContent>
      </HeroSection>

      <Section>
        <Container>
          <Grid>
            <Info>
              <InfoCard>
                <IconBox><FiPhone /></IconBox>
                <InfoContent>
                  <InfoLabel>Phone</InfoLabel>
                  <InfoValue><a href="tel:8024587848">(802) 458-7848</a></InfoValue>
                </InfoContent>
              </InfoCard>
              <InfoCard>
                <IconBox><FiMail /></IconBox>
                <InfoContent>
                  <InfoLabel>Email</InfoLabel>
                  <InfoValue><a href="mailto:thehonestmechanicvt@gmail.com">thehonestmechanicvt@gmail.com</a></InfoValue>
                </InfoContent>
              </InfoCard>
              <InfoCard>
                <IconBox><FiMapPin /></IconBox>
                <InfoContent>
                  <InfoLabel>Location</InfoLabel>
                  <InfoValue>5692 Ethan Allen Hwy, Charlotte, VT</InfoValue>
                </InfoContent>
              </InfoCard>
              <InfoCard>
                <IconBox><FiClock /></IconBox>
                <InfoContent>
                  <InfoLabel>Hours</InfoLabel>
                  <InfoValue>Mon-Fri: 9am-5pm</InfoValue>
                </InfoContent>
              </InfoCard>
            </Info>

            <Form ref={formRef} onSubmit={handleSubmit}>
            {status === 'success' && (
              <StatusMsg $success><FiCheck /> Thanks for reaching out! We'll get back to you soon.</StatusMsg>
            )}
            {status === 'error' && (
              <StatusMsg><FiAlertCircle /> Something went wrong. Please call us or try again.</StatusMsg>
            )}
              <FormRow>
                <Input
                  type="text"
                  name="user_name"
                  placeholder="Your Name"
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  required
                />
                <Input
                  type="email"
                  name="user_email"
                  placeholder="Your Email"
                  value={form.email}
                  onChange={(e) => setForm({...form, email: e.target.value})}
                  required
                />
              </FormRow>
              <FormGroup>
                <Input
                  type="tel"
                  name="user_phone"
                  placeholder="Your Phone"
                  value={form.phone}
                  onChange={(e) => setForm({...form, phone: e.target.value})}
                />
              </FormGroup>
              <FormGroup>
                <TextArea
                  name="message"
                  placeholder="Tell us about your vehicle and what you need..."
                  value={form.message}
                  onChange={(e) => setForm({...form, message: e.target.value})}
                  required
                />
              </FormGroup>
              <SubmitBtn type="submit" disabled={sending}>
                {sending ? 'Sending...' : 'Send Message'} {!sending && <FiSend />}
              </SubmitBtn>
            </Form>
          </Grid>
        </Container>
      </Section>

      <MapSection>
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2844.5!2d-73.2!3d44.3!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDTCsDE4JzAwLjAiTiA3M8KwMTInMDAuMCJX!5e0!3m2!1sen!2sus!4v1600000000000!5m2!1sen!2sus"
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="The Honest Mechanic Location"
        />
      </MapSection>
    </PageWrapper>
  );
}
